import { useEffect, useState } from "react";
import { MENU as DEFAULT_MENU } from "../data/menu";
import { fetchMenu, saveMenu as saveMenuApi, resetMenuApi, ApiError } from "../api";
import { forceLogout } from "./useSettings";

const listeners = new Set();
let currentMenu = DEFAULT_MENU;
let menuLoaded = false;

function cloneDefault() {
  return JSON.parse(JSON.stringify(DEFAULT_MENU));
}

function cloneMenu(menu) {
  return menu.map((c) => ({
    ...c,
    items: c.items.map((i) => ({
      ...i,
      allergens: i.allergens ? [...i.allergens] : undefined,
    })),
  }));
}

function notify(menu) {
  currentMenu = menu;
  listeners.forEach((l) => l(menu));
}

async function loadMenu() {
  try {
    const remote = await fetchMenu();
    if (remote && Array.isArray(remote) && remote.length > 0) {
      return remote;
    }
  } catch {
    /* fall back to default */
  }
  return cloneDefault();
}

async function pushMenu(menu) {
  try {
    const saved = await saveMenuApi(menu);
    notify(saved);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await forceLogout();
    }
    return false;
  }
}

export function useMenu() {
  const [state, setState] = useState({ menu: currentMenu, loaded: menuLoaded });

  useEffect(() => {
    let mounted = true;
    if (!menuLoaded) {
      menuLoaded = true;
      loadMenu().then((m) => {
        if (!mounted) return;
        notify(m);
      });
    }
    const l = (m) => setState({ menu: m, loaded: true });
    listeners.add(l);
    return () => {
      mounted = false;
      listeners.delete(l);
    };
  }, []);

  return {
    menu: state.menu,
    loaded: state.loaded,
    reload: async () => {
      const m = await loadMenu();
      notify(m);
    },
  };
}

export function getCurrentMenu() {
  return currentMenu;
}

// ---------- Mutations (admin) ----------
export async function addItem(categoryId, item) {
  const menu = cloneMenu(currentMenu);
  const cat = menu.find((c) => c.id === categoryId);
  if (!cat) return false;
  cat.items.push(item);
  return pushMenu(menu);
}

export async function updateItem(categoryId, index, item) {
  const menu = cloneMenu(currentMenu);
  const cat = menu.find((c) => c.id === categoryId);
  if (!cat || index < 0 || index >= cat.items.length) return false;
  cat.items[index] = item;
  return pushMenu(menu);
}

export async function deleteItem(categoryId, index) {
  const menu = cloneMenu(currentMenu);
  const cat = menu.find((c) => c.id === categoryId);
  if (!cat || index < 0 || index >= cat.items.length) return false;
  cat.items.splice(index, 1);
  return pushMenu(menu);
}

export async function moveItem(categoryId, index, direction) {
  const menu = cloneMenu(currentMenu);
  const cat = menu.find((c) => c.id === categoryId);
  if (!cat) return false;
  const target = index + direction;
  if (target < 0 || target >= cat.items.length) return false;
  const [moved] = cat.items.splice(index, 1);
  cat.items.splice(target, 0, moved);
  return pushMenu(menu);
}

export async function resetMenu() {
  try {
    await resetMenuApi();
    const fresh = cloneDefault();
    notify(fresh);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await forceLogout();
    }
    return false;
  }
}

export function parseAllergens(text) {
  const parts = (text || "")
    .split(/[,;\s]+/)
    .map((p) => parseInt(p, 10))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 14);
  if (parts.length === 0) return undefined;
  return Array.from(new Set(parts)).sort((a, b) => a - b);
}

export function formatAllergens(a) {
  return a && a.length ? a.join(", ") : "";
}
